import React, { FC, MutableRefObject, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { CREATE_ORDER_SIDE, ORDER_SIDE, ORDER_TYPE, PRICE_TYPE } from 'src/common/constants';
import {
    Button,
    Checkbox,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Tooltip,
} from 'src/components/core';
import { Cross, Info } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { APP_URLS } from 'src/setup/client/app-urls';
import { setSymbolSettings } from 'src/setup/redux/slices/derivativesSlice';
import { FutureFormElements, FutureSymbol } from 'src/types';

interface ConfirmationModalProps {
    isOpen: boolean;
    higherPriceCheck: boolean;
    lowerPriceCheck: boolean;
    triggerPriceCheck: boolean;
    values: FutureFormElements;
    currentSymbol: FutureSymbol;
    orderCost: number;
    orderValue: number;
    orderFee: number;
    orderButtonRef: MutableRefObject<string>;
    liquidationPrice: string;
    handleClose: () => void;
    handleSubmit: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { typography, isMobile } }) => `
        .modal-paper {
            width: ${isMobile ? `343px` : `326px`};
        }
        .modal-title {
            padding: ${isMobile ? `17px 16px` : `16px`};
            > div {
                font-size: ${
                    isMobile
                        ? typography.size.mobile.paragraph.p1
                        : typography.size.desktop.paragraph.p1
                };
                line-height: ${
                    isMobile
                        ? `calc(${typography.lineHeight[3]} - 0.5px)`
                        : `${typography.lineHeight[4]}`
                };
            }
        }
        .modal-content {
            padding: 0 16px;
            .setting-item {
                justify-content: space-between;
                align-items: center;
                margin-top: ${isMobile ? '12px' : '16px'};
                :last-of-type {
                    margin-top: ${isMobile ? '16px' : '24px'};
                }
                font-size: ${
                    isMobile
                        ? typography.size.mobile.paragraph.p2
                        : typography.size.desktop.paragraph.p2
                };
                line-height: ${
                    isMobile
                        ? `calc(${typography.lineHeight[2]} - 1px)`
                        : `calc(${typography.lineHeight[3]} - 0.5px)`
                };
                > div:last-of-type {
                    overflow-wrap: anywhere;
                }
            }
        }
        .modal-actions {
            padding: 24px 16px 16px;
            justify-content: center;
            gap: 16px;
            > button {
                line-height: ${
                    isMobile
                        ? `calc(${typography.lineHeight[3]} - 1px)`
                        : `calc(${typography.lineHeight[3]} - 0.5px)`
                };
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors } }) => `
        color: ${colors.text.tertiary};
        padding-right: 10px;
        display: flex;
        align-items: center;
        gap: 8px;
    `}
`;
const ValueText = styled.div`
    ${({ theme: { typography }, color }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        word-break: break-word;
        text-align: right;
        ${color ? `color: ${color}` : ''}
    `}
`;

const TitleText = styled.div`
    ${({ color }) => `
        color: ${color};
        display: inline-block;
    `}
`;

const StyledHr = styled.hr`
    ${({ theme: { colors, isMobile } }) => `
        border-color: ${colors.border.dark};
        width: 100%;
        height: 1px;
        margin: 0;
        margin-top: ${isMobile ? '0' : '8px'};
        margin-bottom: ${isMobile ? '0' : '8px'};
    `}
`;

const ConfirmationModal: FC<ConfirmationModalProps> = ({
    isOpen,
    higherPriceCheck,
    lowerPriceCheck,
    triggerPriceCheck,
    values,
    orderCost,
    orderValue,
    orderFee,
    orderButtonRef,
    currentSymbol,
    liquidationPrice,
    handleClose,
    handleSubmit,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { colors } = useTheme();
    const { allowTrading } = useKycAndFeaturesConfig();

    const [isConfirm, setIsConfirm] = useState(false);
    const { symbolId, exchangeId, symbolName, quoteTokenName, secondLevelUnderlyingName } =
        currentSymbol;

    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);

    const handleCancel = () => {
        handleClose();
        setIsConfirm(false);
    };

    const handleConfirm = () => {
        if (!allowTrading) return;

        if (isConfirm) {
            dispatch(
                setSymbolSettings({
                    exchange_id: exchangeId,
                    symbol_ids: symbolId,
                    is_confirm: !isConfirm,
                    account_index: userCurrentFutureTradeAccountIndex,
                }),
            );
        }
        handleSubmit();
        handleCancel();
    };

    return (
        <StyledModal size="sm" open={isOpen}>
            <ModalTitle>
                <div>
                    <TitleText
                        color={
                            orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                                ? colors.alert.green
                                : colors.alert.red
                        }
                    >
                        {values.orderSide === ORDER_SIDE.OPEN ? (
                            <>
                                {`${t(values.orderType.toLowerCase())} ${t(
                                    orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                                        ? 'long'
                                        : 'short',
                                )}`}
                            </>
                        ) : (
                            <>
                                {`${t(values.orderSide.toLowerCase())} ${t(
                                    orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                                        ? 'short'
                                        : 'long',
                                )}`}
                            </>
                        )}
                    </TitleText>{' '}
                    {symbolName}
                </div>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleCancel}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                {values.orderType === ORDER_TYPE.CONDITIONAL && (
                    <FlexContainer className="setting-item">
                        <LabelText>{t('trigger_price')}</LabelText>
                        <ValueText>
                            {values.triggerPrice} {quoteTokenName}
                        </ValueText>
                    </FlexContainer>
                )}
                <FlexContainer className="setting-item">
                    <LabelText>{t('order_price')}</LabelText>
                    <ValueText>
                        {`${t(
                            values.priceType !== PRICE_TYPE.MARKET
                                ? values.price
                                : 'last_traded_price',
                        )} ${values.priceType !== PRICE_TYPE.MARKET ? quoteTokenName : ''}`}
                    </ValueText>
                </FlexContainer>
                <FlexContainer className="setting-item">
                    <LabelText>{t('quantity')}</LabelText>
                    <ValueText>
                        {values.quantity} {secondLevelUnderlyingName}
                    </ValueText>
                </FlexContainer>
                {values.orderSide === ORDER_SIDE.OPEN && (
                    <>
                        <FlexContainer className="setting-item">
                            <LabelText>{t('order_margin')}</LabelText>
                            <ValueText>
                                {orderCost} {quoteTokenName}
                            </ValueText>
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <LabelText>{t('order_value')}</LabelText>
                            <ValueText>
                                {orderValue} {quoteTokenName}
                            </ValueText>
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <StyledHr />
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <LabelText>
                                {t('est_fee')}
                                <Tooltip
                                    overlay={
                                        <Trans
                                            i18nKey={'fee_disclaimer'}
                                            components={{
                                                MyFeeUrl: (
                                                    <a
                                                        target="_blank"
                                                        href={APP_URLS.FOOTER_URLS.TRADING_FEE}
                                                        rel="noreferrer"
                                                        style={{
                                                            color: colors.primary,
                                                            borderBottom: '1px solid',
                                                        }}
                                                    />
                                                ),
                                            }}
                                        />
                                    }
                                    interactive
                                >
                                    <Info fill={colors.text.disabled} size={15} />
                                </Tooltip>
                            </LabelText>
                            <ValueText>
                                {orderFee} {quoteTokenName}
                            </ValueText>
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <LabelText>{t('est_liq_price')}</LabelText>
                            <ValueText color={colors.alert.yellow}>
                                {liquidationPrice} {quoteTokenName}
                            </ValueText>
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <LabelText>{t('leverage')}</LabelText>
                            <ValueText>{values.leverage}x</ValueText>
                        </FlexContainer>
                        <FlexContainer className="setting-item">
                            <LabelText>{t('time_in_force')}</LabelText>
                            <ValueText>
                                {`${t(`${values.timeInForce.toLowerCase()}_a`)} (
                                ${values.timeInForce})`}
                            </ValueText>
                        </FlexContainer>
                    </>
                )}
                <FlexContainer className="setting-item">
                    {higherPriceCheck || lowerPriceCheck || triggerPriceCheck ? (
                        t(
                            higherPriceCheck
                                ? 'price_of_placed_order_higher'
                                : lowerPriceCheck
                                ? 'price_of_placed_order_lower'
                                : 'price_of_placed_order_trigger',
                        )
                    ) : (
                        <Checkbox
                            value={isConfirm}
                            isDefault={true}
                            label={t('not_show_again')}
                            onChange={setIsConfirm}
                        />
                    )}
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button
                    disabled={!allowTrading}
                    styles={{
                        background: `${
                            orderButtonRef.current === CREATE_ORDER_SIDE.BUY
                                ? colors.alert.green
                                : colors.alert.red
                        } !important`,
                    }}
                    color={colors.brand.main}
                    fullWidth
                    onClick={handleConfirm}
                >
                    {t('confirm')}
                </Button>
                <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleCancel}
                    color={colors.text.tertiary}
                >
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { ConfirmationModal };
